import { useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Plus, Search, Users } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { ContactCard } from "@/components/sartn/ContactCard";
import { EmptyState } from "@/components/sartn/EmptyState";
import { ErrorState } from "@/components/sartn/ErrorState";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useContactStore } from "@/stores/contactStore";
import { cn } from "@/lib/cn";
import type { ContactCategory } from "@/components/sartn/CategoryBadge";

const CATEGORY_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "family", label: "Family" },
  { value: "friend", label: "Friend" },
  { value: "business", label: "Business" },
  { value: "vip", label: "VIP" },
  { value: "other", label: "Other" },
];

const LIST_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.03 },
  },
};

const LIST_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    },
  },
};

export default function ContactsPage() {
  const {
    contacts,
    isLoading,
    error,
    searchQuery,
    activeCategory,
    fetchContacts,
    setSearchQuery,
    setActiveCategory,
    clearError,
  } = useContactStore();

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    fetchContacts(activeCategory, searchQuery);
  }, [fetchContacts, activeCategory, searchQuery]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchContacts(activeCategory, value);
      }, 300);
    },
    [activeCategory, fetchContacts, setSearchQuery],
  );

  function handleCategorySelect(category: string) {
    setActiveCategory(category);
    fetchContacts(category, searchQuery);
  }

  function navigateToContact(contactId: number) {
    window.location.hash = `/contacts/${contactId}`;
  }

  function navigateToAddContact() {
    window.location.hash = "/contacts/add";
  }

  const addButton = (
    <button
      type="button"
      onClick={navigateToAddContact}
      className="flex h-10 w-10 items-center justify-center rounded-full
                 text-[var(--color-primary)] transition-colors
                 active:scale-[0.95] active:bg-[var(--color-surface)]"
      aria-label="Add contact"
    >
      <Plus className="h-5 w-5" />
    </button>
  );

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Contacts" onBack={() => window.history.back()} rightAction={addButton} />

      <main className="flex-1 px-4 pb-24 pt-2">
        <SearchBar value={searchQuery} onChange={handleSearchChange} />

        <CategoryFilterBar
          activeCategory={activeCategory}
          onSelect={handleCategorySelect}
        />

        {isLoading && <LoadingSkeleton variant="list" count={6} className="mt-2" />}

        {error && !isLoading && (
          <ErrorState
            message={error}
            onRetry={() => {
              clearError();
              fetchContacts(activeCategory, searchQuery);
            }}
          />
        )}

        {!isLoading && !error && contacts.length === 0 && (
          <EmptyState
            icon={Users}
            title="No contacts yet"
            description="Your Telegram contacts will appear here. You can also add contacts manually."
            actionLabel="Add Contact"
            onAction={navigateToAddContact}
          />
        )}

        {!isLoading && !error && contacts.length > 0 && (
          <>
            <motion.div
              variants={LIST_CONTAINER_VARIANTS}
              initial="hidden"
              animate="visible"
              className="flex flex-col gap-2"
            >
              {contacts.map((contact) => (
                <motion.div key={contact.id} variants={LIST_ITEM_VARIANTS}>
                  <ContactCard
                    contact={contact}
                    onClick={() => navigateToContact(contact.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative mb-3">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
      <input
        type="text"
        placeholder="Search contacts..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[44px] w-full rounded-[var(--radius-lg)] bg-[var(--color-surface)]
                   py-2.5 pl-10 pr-4 text-sm shadow-sm
                   text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]
                   outline-none transition-colors
                   focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
      />
    </div>
  );
}

function CategoryFilterBar({
  activeCategory,
  onSelect,
}: {
  activeCategory: string;
  onSelect: (category: string) => void;
}) {
  return (
    <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto">
      {CATEGORY_FILTERS.map((filter) => {
        const isActive = activeCategory === filter.value;
        return (
          <button
            key={filter.value}
            type="button"
            onClick={() => onSelect(filter.value)}
            className={cn(
              "inline-flex min-h-[44px] shrink-0 items-center rounded-[var(--radius-full)] px-3.5 text-xs font-medium",
              "transition-colors",
              isActive
                ? "bg-[var(--color-primary)] text-white"
                : "bg-[var(--color-surface)] text-[var(--color-text-secondary)]",
            )}
          >
            {filter.label}
          </button>
        );
      })}
    </div>
  );
}
